import {
  Alert,
  AlertDescription,
  AlertIcon,
  VStack,
  Button,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  FormLabel,
} from '@chakra-ui/react';
import {
  ColumnDef,
  useReactTable,
  getPaginationRowModel,
  flexRender,
  getCoreRowModel,
} from '@tanstack/react-table';
import React, { useCallback, useEffect, useState } from 'react';

import { Bank } from '@finley-technologies/finley-types';
import {
  parse,
  getCollateralAccountsEffectiveAdvanceRate,
  getCollateralAccountsTotalLendableValue,
  getCollateralAccountsTotalMarketValue,
  getTotalLoanOutstanding,
  getAssignedCollateralAccountsCount,
} from '@finley-technologies/valley-bank-utils';
import type { ValleyBankCustomerWithRelationships } from '@finley-technologies/valley-bank-utils';

import {
  GetValleyBankCustomersQueryGQLType,
  useGetValleyBankCustomersLazyQueryGQLType,
  ValleyBankCustomerQueryParamsGQLType,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { colors } from '~/packages/common-frontend/colors';
import { ProgressAlert } from '~/packages/common-frontend/components/Alert';
import {
  CsvXlsxDownloadSelect,
  getTanstackVisibleDataForDownload,
} from '~/packages/common-frontend/components/CsvXlsxDownloadSelect';
import { Icon } from '~/packages/common-frontend/components/icons';
import { Select } from '~/packages/common-frontend/components/Select';
import { ColumnDisplaySettingsButton } from '~/packages/common-frontend/components/TanStack/ColumnReordering';
import {
  createDefaultTableColumnConfiguration,
  getColumnNames,
  TableColumnConfigurationKey,
  useTableColumnPreferences,
} from '~/packages/common-frontend/hooks/useTableColumnPreferences';
import { frozenHeaderRowStyling } from '~/packages/common-frontend/styles';
import {
  scrollToTopOfElement,
  formatCurrencyAmount,
  percentageToPercentageLabel,
} from '~/packages/common-frontend/utils';
import { SearchPopover } from '~/valleyBank/domains/components/SearchPopover';
import { CustomerSummaryLink } from '~/valleyBank/domains/Customers/hooks/CustomerContext';
import { useDataMetadataContext } from '~/valleyBank/domains/page/hooks/DataMetadataContext';

const TABLE_KEY: TableColumnConfigurationKey = 'valleyBankSbl.customers';

const DEFAULT_TABLE_CONFIG = createDefaultTableColumnConfiguration({
  tableKey: TABLE_KEY,
  columns: [
    { columnKey: 'cifNum', isVisible: true, isFixed: false },
    { columnKey: 'fullName', isVisible: true, isFixed: false },
    { columnKey: 'total_market_value', isVisible: true, isFixed: false },
    { columnKey: 'advanceRate', isVisible: true, isFixed: false },
    { columnKey: 'totalLendableValue', isVisible: true, isFixed: false },
    { columnKey: 'totalOutstandingBalance', isVisible: true, isFixed: false },
    { columnKey: 'numberCollateralAccounts', isVisible: true, isFixed: false },
    { columnKey: 'numberAssignedAccounts', isVisible: true, isFixed: false },
    { columnKey: 'numberLoanAccounts', isVisible: true, isFixed: false },
  ],
});

const COLUMN_DEFS: ColumnDef<ValleyBankCustomerWithRelationships, any>[] = [
  {
    id: 'cifNum',
    accessorKey: 'cif_num',
    header: 'CIF Number',
    cell: function CifNumber({ row, getValue }) {
      return (
        <CustomerSummaryLink cifnum={row.original.cif_num}>
          {getValue()}
        </CustomerSummaryLink>
      );
    },
  },
  {
    id: 'fullName',
    accessorKey: 'full_name',
    header: 'Full Name',
    cell: function CifNumber({ row, getValue }) {
      return (
        <CustomerSummaryLink cifnum={row.original.cif_num}>
          {getValue()}
        </CustomerSummaryLink>
      );
    },
  },
  {
    id: 'numberCollateralAccounts',
    header: 'Number of Collateral Accounts',
    accessorFn: (row) => row.collateralAccounts.length,
  },
  {
    id: 'total_market_value',
    header: 'Total Market Value',
    accessorFn: ({ collateralAccounts }) =>
      formatCurrencyAmount(
        getCollateralAccountsTotalMarketValue(collateralAccounts),
        Bank.Currency.USD, // TODO do I have to update this based on currency
      ),
  },
  {
    id: 'numberAssignedAccounts',
    header: 'Number of Assigned Accounts',
    accessorFn: ({ collateralAccounts }) =>
      getAssignedCollateralAccountsCount(collateralAccounts),
  },
  {
    id: 'advanceRate',
    header: 'Advance Rate',
    accessorFn: ({ collateralAccounts }) =>
      percentageToPercentageLabel(
        getCollateralAccountsEffectiveAdvanceRate(collateralAccounts) * 100,
        2,
      ),
  },
  {
    id: 'totalLendableValue',
    header: 'Total Lending Value',
    accessorFn: ({ collateralAccounts }) =>
      formatCurrencyAmount(
        getCollateralAccountsTotalLendableValue(collateralAccounts),
        Bank.Currency.USD, // TODO do I have to update this based on currency
      ),
  },
  {
    id: 'numberLoanAccounts',
    header: 'Number of Loan Accounts',
    accessorFn: ({ loanAccounts }) => loanAccounts.length,
  },
  {
    id: 'totalOutstandingBalance',
    header: 'Total Outstanding Balance',
    accessorFn: ({ loanAccounts }) =>
      formatCurrencyAmount(
        getTotalLoanOutstanding(loanAccounts),
        Bank.Currency.USD,
      ),
  },
];

const COLUMN_NAMES = getColumnNames(COLUMN_DEFS);

const PAGINATION_LIMIT = 100;

const SEARCH_BY_OPTIONS = [
  { value: 'customerName' as const, label: 'Customer Name' },
  { value: 'cifNum' as const, label: 'CIF Number' },
];

type SearchByOptionType = typeof SEARCH_BY_OPTIONS[number]['value'];

type PaginationControlButtonsProps = Partial<{
  isPreviousDisabled: boolean;
  isNextDisabled: boolean;
  onClickPrevious: () => void;
  onClickNext: () => void;
}>;
const PaginationControlButtons = ({
  isPreviousDisabled,
  onClickPrevious,
  isNextDisabled,
  onClickNext,
}: PaginationControlButtonsProps) => {
  return (
    <HStack justify="space-between" mt="32px" w="100%">
      <Button
        variant="ghost"
        isDisabled={isPreviousDisabled || isPreviousDisabled === undefined}
        onClick={onClickPrevious}
      >
        Previous
      </Button>
      <Button
        variant="ghost"
        isDisabled={isNextDisabled || isNextDisabled === undefined}
        onClick={onClickNext}
      >
        Next
      </Button>
    </HStack>
  );
};

type PaginationControlProps = {
  paginationInfo?: GetValleyBankCustomersQueryGQLType['valleyBankCustomers']['paginationInfo'];
  fetchCustomerPage: (input: { limit: number; offset: number }) => void;
};
const PaginationControl = ({
  fetchCustomerPage,
  paginationInfo,
}: PaginationControlProps) => {
  if (!paginationInfo) {
    return <PaginationControlButtons />;
  }

  const { totalCount, offset: currentOffset, limit } = paginationInfo;

  const hasPrevious = currentOffset > 0;
  const hasNext = currentOffset + limit < totalCount;

  return (
    <PaginationControlButtons
      isNextDisabled={!hasNext}
      isPreviousDisabled={!hasPrevious}
      onClickNext={() =>
        fetchCustomerPage({ limit, offset: currentOffset + limit })
      }
      onClickPrevious={() =>
        fetchCustomerPage({ limit, offset: currentOffset - limit })
      }
    />
  );
};

type CustomerSearchProps = {
  searchCustomerName: string;
  searchCifNum: string;
  setSearchCustomerName: (value: string) => void;
  setSearchCifNum: (value: string) => void;
  setQueryParams: (value: ValleyBankCustomerQueryParamsGQLType) => void;
  fetchInitialCustomerSearchPage: (input: {
    cifNum?: string;
    customerName?: string;
  }) => void;
};
const CustomerSearch = ({
  searchCifNum,
  searchCustomerName,
  setSearchCifNum,
  setSearchCustomerName,
  setQueryParams,
  fetchInitialCustomerSearchPage,
}: CustomerSearchProps) => {
  const [searchBy, setSearchBy] = useState<SearchByOptionType>(
    SEARCH_BY_OPTIONS[0].value, // default to `customerName`
  );

  const handleClear = useCallback(() => {
    if (searchBy === 'cifNum') {
      setSearchCifNum('');
    } else if (searchBy === 'customerName') {
      setSearchCustomerName('');
    }
  }, [searchBy, setSearchCifNum, setSearchCustomerName]);

  const handleSearch = useCallback(() => {
    if (searchBy === 'cifNum') {
      setQueryParams({ cifNum: searchCifNum });
      fetchInitialCustomerSearchPage({ cifNum: searchCifNum });
    } else if (searchBy === 'customerName') {
      setQueryParams({ customerName: searchCustomerName });
      fetchInitialCustomerSearchPage({ customerName: searchCustomerName });
    }
  }, [
    fetchInitialCustomerSearchPage,
    searchBy,
    searchCifNum,
    searchCustomerName,
    setQueryParams,
  ]);

  return (
    <SearchPopover
      dropdownTitle="Search Customers"
      onClear={handleClear}
      onSearch={handleSearch}
      bodyChildren={
        <VStack w="100%" spacing="24px">
          <HStack w="100%" spacing="16px">
            <FormLabel m="auto">Search By</FormLabel>
            <Select
              options={SEARCH_BY_OPTIONS}
              value={searchBy}
              onChange={(nextValue) => {
                if (nextValue) {
                  if (nextValue === 'cifNum') {
                    setSearchCustomerName('');
                  } else if (nextValue === 'customerName') {
                    setSearchCifNum('');
                  }
                  setSearchBy(nextValue);
                }
              }}
            />
          </HStack>
          {searchBy === 'cifNum' && (
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <Icon icon="magnifying-glass" />
              </InputLeftElement>
              <Input
                value={searchCifNum}
                onChange={(e) => setSearchCifNum(e.target.value)}
                placeholder="CIF Number"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
              />
            </InputGroup>
          )}
          {searchBy === 'customerName' && (
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <Icon icon="magnifying-glass" />
              </InputLeftElement>
              <Input
                value={searchCustomerName}
                onChange={(e) => setSearchCustomerName(e.target.value)}
                placeholder="Customer Name"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
              />
            </InputGroup>
          )}
        </VStack>
      }
    />
  );
};

export const CustomersTable = () => {
  const { reportingDate, isPreview, setAsOfDate } = useDataMetadataContext();
  const {
    columnConfiguration,
    columnOrder,
    columnVisibility,
    updateUserColumnConfiguration,
  } = useTableColumnPreferences({
    tableKey: TABLE_KEY,
    defaultTableColumnConfiguration: DEFAULT_TABLE_CONFIG,
  });
  const [customers, setCustomers] = useState<
    ValleyBankCustomerWithRelationships[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchCifNum, setSearchCifNum] = useState<string>('');
  const [searchCustomerName, setSearchCustomerName] = useState<string>('');
  const [
    queryParams,
    setQueryParams,
  ] = useState<ValleyBankCustomerQueryParamsGQLType | null>(null);

  const [getCustomers, { data }] = useGetValleyBankCustomersLazyQueryGQLType({
    fetchPolicy: 'network-only',
    onCompleted: (res) => {
      const newCustomers = parse.parseValleyBankCustomers(
        res.valleyBankCustomers.customers,
      );
      if (newCustomers[0]) {
        setAsOfDate(newCustomers[0].asOfDate);
      }
      setCustomers(newCustomers);
      setIsLoading(false);
    },
  });

  const fetchCustomerPage = useCallback(
    ({ limit, offset }: { limit: number; offset: number }) => {
      getCustomers({
        variables: {
          reportingDate,
          isPreview,
          paginationOptions: { limit, offset },
          queryParams,
        },
      });
      scrollToTopOfElement('valley-bank-customers-table');
    },
    [getCustomers, isPreview, queryParams, reportingDate],
  );

  const fetchInitialCustomerSearchPage = useCallback(
    (queryParam: { cifNum?: string; searchCifNum?: string }) => {
      setIsLoading(true);
      setCustomers([]);
      getCustomers({
        variables: {
          reportingDate,
          isPreview,
          paginationOptions: { limit: PAGINATION_LIMIT, offset: 0 },
          queryParams: queryParam,
        },
      });
    },
    [getCustomers, isPreview, reportingDate],
  );

  // only want to do this if we want to show a loading state when initial load (or metadata changes)
  useEffect(() => {
    setIsLoading(true);
    setCustomers([]);
    getCustomers({
      variables: {
        reportingDate,
        isPreview,
        paginationOptions: { limit: PAGINATION_LIMIT, offset: 0 },
      },
    });
  }, [getCustomers, isPreview, reportingDate]);

  const table = useReactTable({
    data: customers,
    columns: COLUMN_DEFS,
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: PAGINATION_LIMIT,
      },
    },
    state: {
      columnOrder,
      columnVisibility,
    },
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const tableDataForDownload = getTanstackVisibleDataForDownload({
    tableInstance: table,
  });

  return (
    <VStack w="100%" h="100%" justify="flex-start" spacing="24px">
      <HStack w="100%" spacing="16px" justifyContent="flex-end">
        <CustomerSearch
          searchCifNum={searchCifNum}
          searchCustomerName={searchCustomerName}
          setSearchCifNum={setSearchCifNum}
          setSearchCustomerName={setSearchCustomerName}
          setQueryParams={setQueryParams}
          fetchInitialCustomerSearchPage={fetchInitialCustomerSearchPage}
        />
        <ColumnDisplaySettingsButton
          initialColumns={columnConfiguration}
          updateColumnConfigs={updateUserColumnConfiguration}
          columnNames={COLUMN_NAMES}
        />
        {customers.length > 0 && (
          <CsvXlsxDownloadSelect
            dataRows={tableDataForDownload.dataRows}
            headers={tableDataForDownload.headers}
            filenamePrefix="customers"
          />
        )}
      </HStack>
      {isLoading && (
        <ProgressAlert status="info">Loading Customers</ProgressAlert>
      )}
      {!isLoading && customers.length === 0 && (
        <Alert status="info">
          <AlertIcon />
          <AlertDescription>No Customers found</AlertDescription>
        </Alert>
      )}
      {customers.length > 0 && (
        <>
          <TableContainer
            mt="32px"
            borderWidth="medium"
            borderColor={colors.gray[200]}
            // maxH required for table top sticky header to work
            // since position:sticky attaches to the nearest ancestor with a scrolling mechanism
            // https://stackoverflow.com/questions/70770027/table-with-sticky-header-and-horizontal-scroll
            maxHeight="50vh"
            overflowX="auto"
            overflowY="auto"
            position="relative"
            id="receivables-dashboard-main-table"
            w="100%"
          >
            <Table id="valley-bank-customers-table">
              <Thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <Tr key={headerGroup.id} style={frozenHeaderRowStyling}>
                    {headerGroup.headers.map((header) => {
                      return (
                        <Th
                          key={header.id}
                          colSpan={header.colSpan}
                          textTransform="capitalize"
                          padding={
                            header.column.columnDef.meta?.style?.padding ||
                            '8px 16px 8px 16px'
                          }
                          fontSize="16px"
                          fontWeight="medium"
                          whiteSpace="nowrap"
                          color={colors.black[95]}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                        </Th>
                      );
                    })}
                  </Tr>
                ))}
              </Thead>
              <Tbody>
                {table.getRowModel().rows.map((row) => (
                  <Tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <Td
                        key={cell.id}
                        textAlign={cell.column.columnDef.meta?.style?.textAlign}
                        padding={cell.column.columnDef.meta?.style?.padding}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </Td>
                    ))}
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
          <PaginationControl
            fetchCustomerPage={fetchCustomerPage}
            paginationInfo={data?.valleyBankCustomers.paginationInfo}
          />
        </>
      )}
    </VStack>
  );
};
